import { computed, inject, provide, reactive, toValue, watch } from 'vue'
import { useStore } from 'vuex'

import { tracker } from '@/tracking/amplitude'
import { EVENT_NAMES } from '@/tracking/amplitude-helpers'
import { useDisabledModules } from '@/utils/cross-platform/disabled-modules'
import { ASSIGNEE_ENTITY_KEYS } from '@/utils/entity-keys'
import {
  isAzureAppInjectionKey,
  onboardingAiBillingCompleteInjectionKey,
  onboardingAiStepContextInjectionKey
} from '@/utils/injection-keys'

/** Backend synchronized onboarding state enum. */
export const ONBOARDING_STATE = {
  INTRO: 1,
  WEBSITE: 2,
  GOALS: 3,
  STRUCTURE: 4,
  INTERVAL: 5,
  REVIEW: 6,
  INVITE: 7,
  BILLING: 8,
  BOOK_DEMO: 9,
  COMPLETED: 200,
  FAILED: 500,
  COMPLETE_ON_FALLBACK: 600
}

/** Backend synchronized onboarding data type enum. */
export const ONBOARDING_DATA_TYPE = {
  COMPANY_WEBSITE_URL: 1,
  COMPANY_SIZE_REQUEST: 2,
  RESEARCH_REQUEST: 3,
  RESEARCH_NOTES: 4,
  OKR_GENERATION_REQUEST: 5,
  GOALS: 6,
  STRUCTURE: 7,
  INTERVAL: 8,
  WORKSPACE_SETUP_REQUEST: 9
}

export const ONBOARDING_DATA_TYPE_TO_STRING_MAPPER = {
  [ONBOARDING_DATA_TYPE.COMPANY_WEBSITE_URL]: 'company_website_url',
  [ONBOARDING_DATA_TYPE.COMPANY_SIZE_REQUEST]: 'company_size_request',
  [ONBOARDING_DATA_TYPE.RESEARCH_REQUEST]: 'research_request',
  [ONBOARDING_DATA_TYPE.RESEARCH_NOTES]: 'research_notes',
  [ONBOARDING_DATA_TYPE.OKR_GENERATION_REQUEST]: 'okr_generation_request',
  [ONBOARDING_DATA_TYPE.GOALS]: 'goals',
  [ONBOARDING_DATA_TYPE.STRUCTURE]: 'structure',
  [ONBOARDING_DATA_TYPE.INTERVAL]: 'interval',
  [ONBOARDING_DATA_TYPE.WORKSPACE_SETUP_REQUEST]: 'workspace_setup_request'
}

/** Local state keys for wizard steps persisted via `dataTypeId`. */
export const ONBOARDING_DATA_KEY = {
  COMPANY_WEBSITE: 'companyWebsite',
  SELECTED_GOALS: 'selectedGoals',
  RECOMMENDED_STRUCTURE_ID: 'recommendedStructureId',
  SELECTED_STRUCTURE_ID: 'selectedStructureId',
  SELECTED_INTERVAL_ID: 'selectedIntervalId',
  RESEARCH_DATA: 'researchData',
  RESEARCH_NOTES: 'researchNotes'
}

/** Backend synchronized onboarding data status enum. */
export const ONBOARDING_DATA_STATUS = {
  PENDING: 1,
  COMPLETED: 2,
  FAILED: 3
}

/** Client-only steps that are not persisted as backend `stateId`. */
export const ONBOARDING_TRANSIENT_STATE = {
  COMPANY_SIZE_LOADING: 101,
  RESEARCH_LOADING: 102,
  SETUP_LOADING: 103,
  FAILED_LOADING: 104
}

/** Full step enum: backend states plus transient loading / review screens. */
export const ONBOARDING_STEP = {
  ...ONBOARDING_STATE,
  ...ONBOARDING_TRANSIENT_STATE
}

export const ONBOARDING_AI_GOAL_OPTIONS = [
  { id: 'alignment', labelKey: 'onboarding.ai.goals.option_alignment' },
  { id: 'priorities', labelKey: 'onboarding.ai.goals.option_priorities' },
  { id: 'engagement', labelKey: 'onboarding.ai.goals.option_engagement' },
  { id: 'outcomes', labelKey: 'onboarding.ai.goals.option_outcomes' },
  { id: 'transparency', labelKey: 'onboarding.ai.goals.option_transparency' },
  { id: 'single_source', labelKey: 'onboarding.ai.goals.option_single_source' }
]

export const STRUCTURE_OPTION_IDS = {
  SMALL: 'company-team',
  MEDIUM: 'company-department-team',
  LARGE: 'company-department-team-personal'
}
export const DEFAULT_STRUCTURE_OPTION_ID = STRUCTURE_OPTION_IDS.MEDIUM

/** Backend company size bucket: 1 = 1–50, 2 = 51–500, 3 = 500+. */
export const COMPANY_SIZE_BUCKET_TO_STRUCTURE_ID = {
  1: STRUCTURE_OPTION_IDS.SMALL,
  2: STRUCTURE_OPTION_IDS.MEDIUM,
  3: STRUCTURE_OPTION_IDS.LARGE
}

export const ONBOARDING_AI_STRUCTURE_OPTIONS = [
  {
    id: STRUCTURE_OPTION_IDS.SMALL,
    title: 'Option A',
    employeesKey: 'onboarding.ai.structure.option_a_employees',
    descriptionKey: 'onboarding.ai.structure.option_a_description',
    optionKey: 'onboarding.ai.structure.insight_option_a',
    layers: ['Company', 'Team']
  },
  {
    id: STRUCTURE_OPTION_IDS.MEDIUM,
    title: 'Option B',
    employeesKey: 'onboarding.ai.structure.option_b_employees',
    descriptionKey: 'onboarding.ai.structure.option_b_description',
    optionKey: 'onboarding.ai.structure.insight_option_b',
    layers: ['Company', 'Department', 'Team']
  },
  {
    id: STRUCTURE_OPTION_IDS.LARGE,
    title: 'Option C',
    employeesKey: 'onboarding.ai.structure.option_c_employees',
    descriptionKey: 'onboarding.ai.structure.option_c_description',
    optionKey: 'onboarding.ai.structure.insight_option_c',
    layers: ['Company', 'Department', 'Team', 'Personal']
  }
]

export const ONBOARDING_AI_INTERVAL_OPTION_TYPE = {
  ANNUAL_QUARTERLY: 'annual-quarterly',
  QUARTERLY: 'quarterly'
}

export const ONBOARDING_LOADING_VARIANT = {
  COMPANY_SIZE: 'company-size',
  RESEARCH: 'research',
  SETUP: 'setup'
}

export const ONBOARDING_LOADING_DATA_TYPE_MAPPER = {
  [ONBOARDING_DATA_TYPE.COMPANY_SIZE_REQUEST]: {
    variant: ONBOARDING_LOADING_VARIANT.COMPANY_SIZE
  },
  [ONBOARDING_DATA_TYPE.RESEARCH_REQUEST]: {
    variant: ONBOARDING_LOADING_VARIANT.RESEARCH
  },
  [ONBOARDING_DATA_TYPE.WORKSPACE_SETUP_REQUEST]: {
    variant: ONBOARDING_LOADING_VARIANT.SETUP
  }
}

export const ONBOARDING_LOADING_STEP_MAPPER = {
  [ONBOARDING_STEP.COMPANY_SIZE_LOADING]: {
    dataTypeId: ONBOARDING_DATA_TYPE.COMPANY_SIZE_REQUEST,
    variant: ONBOARDING_LOADING_VARIANT.COMPANY_SIZE
  },
  [ONBOARDING_STEP.RESEARCH_LOADING]: {
    dataTypeId: ONBOARDING_DATA_TYPE.RESEARCH_REQUEST,
    variant: ONBOARDING_LOADING_VARIANT.RESEARCH
  },
  [ONBOARDING_STEP.SETUP_LOADING]: {
    dataTypeId: ONBOARDING_DATA_TYPE.WORKSPACE_SETUP_REQUEST,
    variant: ONBOARDING_LOADING_VARIANT.SETUP
  }
}

const DEFAULT_STEP_ORDER = [
  ONBOARDING_STEP.INTRO,
  ONBOARDING_STEP.WEBSITE,
  ONBOARDING_STEP.GOALS,
  ONBOARDING_STEP.COMPANY_SIZE_LOADING,
  ONBOARDING_STEP.STRUCTURE,
  ONBOARDING_STEP.INTERVAL,
  ONBOARDING_STEP.RESEARCH_LOADING,
  ONBOARDING_STEP.REVIEW,
  ONBOARDING_STEP.SETUP_LOADING,
  ONBOARDING_STEP.INVITE,
  // ONBOARDING_STEP.BILLING,
  ONBOARDING_STEP.BOOK_DEMO
]

export const REDIRECT_STEP_MAPPER = {
  [ONBOARDING_STEP.BILLING]: ONBOARDING_STEP.BOOK_DEMO,
  [ONBOARDING_STEP.INVITE]: ONBOARDING_STEP.BOOK_DEMO
}

/** Logical stepper milestones; loading screens share a group with their related wizard step. */
export const ONBOARDING_STEPPER_GROUP = {
  WEBSITE: 'website',
  GOALS: 'goals',
  STRUCTURE: 'structure',
  INTERVAL: 'interval',
  REVIEW: 'review',
  INVITE: 'invite',
  FINISH: 'finish'
}

const STEP_META = {
  [ONBOARDING_STEP.INTRO]: { kind: 'intro' },
  [ONBOARDING_STEP.WEBSITE]: {
    kind: 'wizard',
    stepperGroup: ONBOARDING_STEPPER_GROUP.WEBSITE,
    dataKey: ONBOARDING_DATA_KEY.COMPANY_WEBSITE,
    dataTypeId: ONBOARDING_DATA_TYPE.COMPANY_WEBSITE_URL
  },
  [ONBOARDING_STEP.GOALS]: {
    kind: 'wizard',
    stepperGroup: ONBOARDING_STEPPER_GROUP.GOALS,
    dataKey: ONBOARDING_DATA_KEY.SELECTED_GOALS,
    dataTypeId: ONBOARDING_DATA_TYPE.GOALS
  },
  [ONBOARDING_STEP.COMPANY_SIZE_LOADING]: {
    kind: 'loading',
    stepperGroup: ONBOARDING_STEPPER_GROUP.GOALS,
    loadingKey: 'COMPANY_SIZE_LOADING',
    hideStepper: true
  },
  [ONBOARDING_STEP.STRUCTURE]: {
    kind: 'wizard',
    stepperGroup: ONBOARDING_STEPPER_GROUP.STRUCTURE,
    dataKey: ONBOARDING_DATA_KEY.SELECTED_STRUCTURE_ID,
    dataTypeId: ONBOARDING_DATA_TYPE.STRUCTURE
  },
  [ONBOARDING_STEP.INTERVAL]: {
    kind: 'wizard',
    stepperGroup: ONBOARDING_STEPPER_GROUP.INTERVAL,
    dataKey: ONBOARDING_DATA_KEY.SELECTED_INTERVAL_ID,
    dataTypeId: ONBOARDING_DATA_TYPE.INTERVAL
  },
  [ONBOARDING_STEP.RESEARCH_LOADING]: {
    kind: 'loading',
    stepperGroup: ONBOARDING_STEPPER_GROUP.INTERVAL,
    loadingKey: 'RESEARCH_LOADING',
    hideStepper: true
  },
  [ONBOARDING_STEP.REVIEW]: {
    kind: 'wizard',
    stepperGroup: ONBOARDING_STEPPER_GROUP.REVIEW,
    dataKey: ONBOARDING_DATA_KEY.RESEARCH_NOTES,
    dataTypeId: ONBOARDING_DATA_TYPE.RESEARCH_NOTES
  },
  [ONBOARDING_STEP.SETUP_LOADING]: {
    kind: 'loading',
    stepperGroup: ONBOARDING_STEPPER_GROUP.REVIEW,
    loadingKey: 'SETUP_LOADING',
    hideStepper: true
  },
  [ONBOARDING_STEP.FAILED_LOADING]: {
    kind: 'failed',
    hideStepper: true
  },
  [ONBOARDING_STEP.INVITE]: {
    kind: 'wizard',
    stepperGroup: ONBOARDING_STEPPER_GROUP.INVITE
  },
  [ONBOARDING_STEP.BILLING]: {
    kind: 'wizard',
    stepperGroup: ONBOARDING_STEPPER_GROUP.FINISH,
    hideStepper: true,
    excludeFromStepCount: true
  },
  [ONBOARDING_STEP.BOOK_DEMO]: {
    kind: 'wizard',
    stepperGroup: ONBOARDING_STEPPER_GROUP.FINISH
  }
}

const isOnboardingStepPrecededByLoader = (step, order = DEFAULT_STEP_ORDER) => {
  if (!step) {
    return false
  }

  const stepIndex = order.indexOf(step)
  if (stepIndex <= 0) {
    return false
  }

  const previousStep = order[stepIndex - 1]
  return STEP_META[previousStep]?.kind === 'loading'
}

export const useOnboardingAiStepResolver = () => {
  const { isBillingModuleDisabled } = useDisabledModules()
  const store = useStore()

  const isAzureApp = inject(isAzureAppInjectionKey)

  const isStepCounted = step => !STEP_META[step]?.excludeFromStepCount

  const userManagementStepNeed = computed(
    () => !isAzureApp || store.state.system.platformApiEnabled
  )

  const stepOrder = computed(() => {
    return DEFAULT_STEP_ORDER.filter(step => {
      if (step === ONBOARDING_STEP.BILLING) return !isBillingModuleDisabled.value
      if (step === ONBOARDING_STEP.INVITE) return userManagementStepNeed.value
      return true
    })
  })

  const wizardSteps = computed(() =>
    stepOrder.value.filter(step => STEP_META[step]?.kind === 'wizard' && isStepCounted(step))
  )
  const interactiveSteps = computed(() =>
    stepOrder.value.filter(
      step => STEP_META[step]?.kind === 'wizard' || STEP_META[step]?.kind === 'intro'
    )
  )

  const wizardStepCount = computed(() => wizardSteps.value.length)

  /** Stepper groups present in the current flow, in order (e.g. invite removed → finish shifts down). */
  const stepperGroupOrder = computed(() => {
    const groups = []
    const seen = new Set()

    stepOrder.value.forEach(step => {
      const group = STEP_META[step]?.stepperGroup
      if (isStepCounted(step) && group && !seen.has(group)) {
        seen.add(group)
        groups.push(group)
      }
    })

    return groups
  })

  const stepperIndexByGroup = computed(() =>
    stepperGroupOrder.value.reduce((map, group, index) => {
      map[group] = index + 1
      return map
    }, {})
  )

  const totalStepperSteps = computed(() => stepperGroupOrder.value.length)

  const wizardStepNumberByStep = computed(() =>
    wizardSteps.value.reduce((map, step, index) => {
      map[step] = index + 1
      return map
    }, {})
  )

  const getStepMeta = step => STEP_META[step] ?? null
  const getStepperIndex = step => {
    const group = getStepMeta(step)?.stepperGroup
    if (!group) {
      return 0
    }

    return stepperIndexByGroup.value[group] ?? 0
  }
  const getWizardStepNumber = step => wizardStepNumberByStep.value[step] ?? 0
  const getNextStep = currentStep => {
    const order = stepOrder.value
    const currentIndex = order.indexOf(currentStep)

    if (currentIndex < 0 || currentIndex >= order.length - 1) {
      return null
    }

    return order[currentIndex + 1]
  }
  const getStepDataKey = step => getStepMeta(step)?.dataKey ?? null
  const getStepDataTypeId = step => getStepMeta(step)?.dataTypeId ?? null

  const isIntroStep = step => step === ONBOARDING_STEP.INTRO
  const isWizardContentStep = step => !isIntroStep(step)
  const isStepperHidden = step => Boolean(getStepMeta(step)?.hideStepper)
  const isBackendState = step => Object.values(ONBOARDING_STATE).includes(step)

  return {
    get stepOrder() {
      return stepOrder.value
    },
    get wizardStepCount() {
      return wizardStepCount.value
    },
    get totalStepperSteps() {
      return totalStepperSteps.value
    },
    interactiveSteps,
    getStepMeta,
    getStepperIndex,
    getWizardStepNumber,
    getNextStep,
    isIntroStep,
    isWizardContentStep,
    isStepperHidden,
    getStepDataKey,
    getStepDataTypeId,
    isBackendState
  }
}

export const provideOnboardingAiStepContext = (stepResolver = null) => {
  const context = reactive({
    currentStep: 0,
    onboardingStep: null,
    sessionId: null,
    totalCount: stepResolver?.totalStepperSteps ?? 0,
    wizardStepCount: stepResolver?.wizardStepCount ?? 0,
    stepOrder: stepResolver?.stepOrder ?? [...DEFAULT_STEP_ORDER],
    hideStepper: false,
    loading: false,
    navigating: false,
    loaderDurationMs: 0
  })

  provide(onboardingAiStepContextInjectionKey, context)

  return context
}

export const useOnboardingAiStepContext = () => {
  return inject(onboardingAiStepContextInjectionKey, null)
}

export const provideOnboardingAiBillingComplete = completeOnboarding => {
  provide(onboardingAiBillingCompleteInjectionKey, completeOnboarding)
}

export const useOnboardingAiBillingComplete = () => {
  return inject(onboardingAiBillingCompleteInjectionKey, null)
}

export const useOnboardingAiWizardStepCount = () => {
  const stepContext = useOnboardingAiStepContext()

  return stepContext?.wizardStepCount ?? 0
}

export const useSyncOnboardingAiStepContext = (
  currentStepIndex,
  stepResolver = null,
  currentOnboardingStep = null
) => {
  const stepContext = useOnboardingAiStepContext()

  watch(
    currentStepIndex,
    stepIndex => {
      if (stepContext) {
        stepContext.currentStep = stepIndex
      }
    },
    { immediate: true }
  )

  if (stepContext && toValue(stepResolver)) {
    watch(
      () => [stepResolver.totalStepperSteps, stepResolver.wizardStepCount, stepResolver.stepOrder],
      ([totalCount, wizardStepCount, stepOrder]) => {
        stepContext.totalCount = totalCount
        stepContext.wizardStepCount = wizardStepCount
        stepContext.stepOrder = stepOrder
      },
      { immediate: true }
    )
  }

  if (stepContext && toValue(stepResolver)) {
    watch(
      currentOnboardingStep,
      step => {
        stepContext.onboardingStep = step
        stepContext.hideStepper = stepResolver.isStepperHidden(step)
      },
      { immediate: true }
    )
  }

  return stepContext
}

const ONBOARDING_VERSION = 'v2'

export const ONBOARDING_TRACKING_STEP_EVENTS = {
  [ONBOARDING_STEP.INTRO]: EVENT_NAMES.ONBOARDING_WELCOME_CLICKED,
  [ONBOARDING_STEP.WEBSITE]: EVENT_NAMES.ONBOARDING_COMPANY_WEBSITE_SUBMITTED,
  [ONBOARDING_STEP.STRUCTURE]: EVENT_NAMES.ONBOARDING_OKR_STRUCTURE_NEXT_CLICKED,
  [ONBOARDING_STEP.INTERVAL]: EVENT_NAMES.ONBOARDING_OKR_INTERVAL_NEXT_CLICKED,
  [ONBOARDING_STEP.REVIEW]: EVENT_NAMES.ONBOARDING_STRATEGIC_PILLARS_COMPLETED,
  [ONBOARDING_STEP.INVITE]: EVENT_NAMES.ONBOARDING_INVITE_LEADERSHIP_COMPLETED_CLICKED
}

export const useOnboardingAiTracking = () => {
  const store = useStore()
  const stepContext = useOnboardingAiStepContext()
  const precededByLoader = computed(() =>
    isOnboardingStepPrecededByLoader(stepContext?.onboardingStep, stepContext?.stepOrder)
  )
  const sessionId = computed(() => String(stepContext?.sessionId ?? ''))

  const getBasePayload = () => ({
    user_id: store.state.system.userData?.[ASSIGNEE_ENTITY_KEYS.USER_ACCOUNT_ID] ?? '',
    session_id: sessionId.value,
    onboarding_version: ONBOARDING_VERSION,

    total_steps: stepContext.wizardStepCount,
    preceded_by_loader: precededByLoader.value,
    loader_duration_ms: precededByLoader.value ? stepContext?.loaderDurationMs ?? 0 : 0
  })

  const logEvent = (eventName, payload = {}) => {
    tracker.logEvent(eventName, {
      ...getBasePayload(),
      ...payload
    })
  }

  return {
    logEvent
  }
}
